import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  /** حذف/اقدام مخرب بدون پس‌زمینه — متن قرمز، هاله فقط روی hover */
  | 'danger-ghost'
  /** اقدام مخربِ Outlined — حاشیهٔ قرمز، بدون پس‌زمینه */
  | 'outline-danger'
  | 'success'
  | 'link'
  /** برای سطوح تیره ناوبری (سایدبار و نوار بالا) */
  | 'nav'
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** آیکون سمت شروع (راست در RTL) */
  iconStart?: ReactNode
  iconEnd?: ReactNode
  fullWidth?: boolean
  /**
   * دکمه فقط‌آیکون — پهنا و بلندا برابر و بدون padding افقی.
   * معمولاً مستقیم ست نمی‌شود؛ `IconButton` آن را می‌دهد.
   */
  iconOnly?: boolean
}

/* ------------------------------------------------------------------
   دو مدلِ دکمه: Filled و Outlined
   ------------------------------------------------------------------
   🔴 هر variant صریحاً یکی از این دو است و حالت سومی وجود ندارد.

   • Filled  — پس‌زمینهٔ پُر، بدون حاشیه: `primary` · `danger` · `success`
   • Outlined — بدون پس‌زمینه، با حاشیهٔ رنگی: `secondary` ·
     `outline-danger` · `ghost`

   ⚠️ پیش‌تر این تفکیک وجود نداشت و نتیجه‌اش سه وزنِ درهم بود:
   `secondary` حاشیه داشت ولی پس‌زمینهٔ سفیدِ پُر هم داشت، و `ghost`
   نه حاشیه داشت نه پس‌زمینه — یعنی تا وقتی موس رویش نمی‌رفت اصلاً
   شبیه دکمه نبود. در کارتِ اقدامِ صفحهٔ جزئیات همین دیده شد: «رد» و
   «برگشت» فقط دو تکه متنِ شناور بودند کنارِ یک دکمهٔ سبزِ پُر.

   ⚠️ `ghost` عمداً بی‌حاشیه ماند: جای استفاده‌اش نوارِ ابزار و ردیفِ
   جدول است، جایی که چند دکمه پشت سر هم می‌آیند و حاشیه‌دادن به همه،
   ردیف را قفس می‌کند. برای «دکمهٔ کم‌وزنِ ولی دیده‌شدنی» حالا
   `secondary` هست.
   ------------------------------------------------------------------ */
const VARIANTS: Record<ButtonVariant, string> = {
  /* ---------- Filled ---------- */
  primary:
    'bg-[var(--pf-primary)] text-[var(--pf-on-primary)] border-transparent ' +
    'hover:bg-[var(--pf-primary-hover)] shadow-[var(--pf-shadow-xs)] ' +
    'hover:shadow-[var(--pf-shadow-sm)]',
  /* ---------- Outlined ---------- */
  /* ⚠️ زمینه شفاف است و نه سفید: روی کارتِ سفید تفاوتی ندارد، ولی
     داخلِ نوارِ خاکستری یا پانوشتِ کارت، مستطیلِ سفید وصله می‌شد.

     ⚠️ حاشیه `--pf-border` است و نه `--pf-border-strong`. وقتی چند
     دکمهٔ Outlined کنار هم می‌نشینند، حاشیهٔ پررنگ هرکدام را به یک
     قابِ مستقل تبدیل می‌کند و ردیف شبیه جدول می‌شود؛ حاشیهٔ ملایم‌تر
     شکلِ دکمه را نگه می‌دارد بی‌آنکه چشم را بگیرد. در حالت hover
     حاشیه پررنگ می‌شود، یعنی خودِ تعاملْ تأکید را می‌آورد. */
  secondary:
    'bg-transparent text-[var(--pf-text)] border-[var(--pf-border)] ' +
    'hover:border-[var(--pf-border-strong)] hover:bg-[var(--pf-surface-hover)]',
  ghost:
    'bg-transparent text-[var(--pf-text)] border-transparent hover:bg-[var(--pf-surface-hover)]',
  /* روی سطح تیره ناوبری. عمداً از --pf-nav-* می‌گیرد نه --pf-text:
     آن توکن در پوسته روشن تقریباً مشکی است و روی نوار تیره نامرئی
     می‌شود. با variant جدا حل شد نه override در محل استفاده، چون
     دو کلاس رنگ متن با specificity برابر تولید می‌شوند و برنده‌شان
     ترتیب خروجی Tailwind است — یعنی نتیجه غیرقابل‌اتکا می‌شد. */
  nav:
    'bg-transparent text-[var(--pf-nav-text)] border-transparent ' +
    'hover:bg-[var(--pf-nav-elevated)] hover:text-[var(--pf-nav-text-hover)]',
  danger:
    'bg-[var(--pf-danger-600)] text-white border-transparent hover:bg-[var(--pf-danger-700)] ' +
    'shadow-[var(--pf-shadow-xs)]',
  /* اقدام مخرب کم‌وزن — برای دکمه حذفِ داخل ردیف که `danger` پرِ قرمز
     برایش سنگین است و در فهرستی از ردیف‌ها چشم را می‌دزدد.
     عمداً --pf-danger-text است نه --pf-danger-600: دومی فقط در پوستهٔ
     روشن تعریف شده و در پوستهٔ تیره همان قرمزِ تیره می‌ماند که روی
     سطح تیره تقریباً نامرئی است. */
  'danger-ghost':
    'bg-transparent text-[var(--pf-danger-text)] border-transparent ' +
    'hover:bg-[var(--pf-danger-50)]',
  /* اقدام مخربِ Outlined — وزنش بین `danger` پُر و `danger-ghost` بی‌مرز
     است. جای استفاده: وقتی اقدامِ مخرب باید صریح دیده شود ولی نباید
     با دکمهٔ اصلیِ همان کارت رقابت کند.

     ⚠️ حاشیه در حالت عادی کم‌رنگ است (`--pf-danger-100`) و فقط متن و
     آیکون قرمزِ کامل‌اند. با حاشیهٔ اشباعِ `--pf-danger-500`، دکمهٔ
     «انصراف» پرصداترین چیزِ کارت می‌شد — بلندتر از خودِ «تأیید» که
     اقدامِ اصلی است. رنگِ متن معنا را می‌رساند؛ حاشیه فقط باید شکلِ
     دکمه را بدهد. در hover پررنگ می‌شود. */
  'outline-danger':
    'bg-transparent text-[var(--pf-danger-text)] border-[var(--pf-danger-100)] ' +
    'hover:border-[var(--pf-danger-500)] hover:bg-[var(--pf-danger-50)]',
  success:
    'bg-[var(--pf-success-600)] text-white border-transparent hover:bg-[var(--pf-success-700)] ' +
    'shadow-[var(--pf-shadow-xs)]',
  link:
    /* لینک متنی = سبز تیره (accent-800)، تنها پله سبزی که به‌عنوان
       متن روی سطح روشن کنتراست AA می‌گیرد */
    'bg-transparent text-[var(--pf-link)] border-transparent hover:underline px-0 h-auto',
}

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-[length:var(--pf-text-xs)] gap-1 rounded-[var(--pf-radius-sm)]',
  sm: 'h-8 px-3 text-[length:var(--pf-text-sm)] gap-1.5 rounded-[var(--pf-radius)]',
  md: 'h-9.5 px-4 text-[length:var(--pf-text-md)] gap-2 rounded-[var(--pf-radius)]',
  lg: 'h-11 px-5 text-[length:var(--pf-text-base)] gap-2 rounded-[var(--pf-radius-md)]',
}

/* همان اندازه‌ها بدون padding افقی و با پهنای برابر بلندا.
   جدا نگه داشته می‌شود تا برای دکمه آیکونی هیچ کلاس `px-*` تولید
   نشود؛ وگرنه باید با کلاسِ متضاد خنثی می‌شد که به ترتیب CSS
   وابسته است. */
const ICON_ONLY_SIZES: Record<ButtonSize, string> = {
  xs: 'size-7 text-[length:var(--pf-text-xs)] gap-1 rounded-[var(--pf-radius-sm)]',
  sm: 'size-8 text-[length:var(--pf-text-sm)] gap-1.5 rounded-[var(--pf-radius)]',
  md: 'size-9.5 text-[length:var(--pf-text-md)] gap-2 rounded-[var(--pf-radius)]',
  lg: 'size-11 text-[length:var(--pf-text-base)] gap-2 rounded-[var(--pf-radius-md)]',
}

function Spinner() {
  return (
    <svg
      className="animate-spin size-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    iconStart,
    iconEnd,
    fullWidth = false,
    disabled,
    className,
    children,
    type = 'button',
    /* فقط برای IconButton — پهنای ثابت به‌جای پهنای اندازه معمولی.
       چرا اینجا و نه با className در IconButton: `cn` یک به‌هم‌چسبانِ
       رشته است نه tailwind-merge، پس اگر IconButton کلاس `px-0` را
       اضافه کند، `px-4` از SIZES هم روی عنصر می‌ماند. هر دو یک
       specificity دارند و برنده ترتیب خروجی Tailwind است — یعنی
       `px-4` که دیرتر تعریف شده. با حذف کلاس در همین‌جا، هیچ‌وقت دو
       کلاس متضاد تولید نمی‌شود. */
    iconOnly = false,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading

  /*
   | اسپینر جای کدام آیکون می‌نشیند.
   |
   | ⚠️ پیش‌تر همیشه جای `iconStart` می‌نشست و `iconEnd` حذف می‌شد. برای
   | دکمه‌ای که فقط `iconEnd` داشت این یعنی دو تغییرِ هم‌زمان در لحظهٔ
   | کلیک — آیکون از یک سمت می‌رفت و اسپینر سمت دیگر می‌آمد — و چون
   | هیچ‌کدام جای دیگری را پر نمی‌کرد، پهنای دکمه هم می‌پرید.
   */
  const spinnerAtEnd = loading && !!iconEnd && !iconStart

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center border font-[var(--pf-weight-medium)]',
        /*
          ⚠️ `transition-colors` به `transition-[colors,box-shadow,transform]`
          گسترش یافت چون حالا سایه و فشردگی هم عوض می‌شوند؛ با
          `transition-colors` آن دو بدون میان‌پرده می‌پریدند.
        */
        'transition-[color,background-color,border-color,box-shadow,transform]',
        'duration-[var(--pf-duration-fast)] select-none',
        /*
          ⚠️ پاسخِ فشردن.

          🔴 دکمه‌ها هیچ حالتِ `:active` نداشتند؛ روی صفحهٔ لمسی که
          hover وجود ندارد، هیچ بازخوردی بین لمس و پاسخِ سرور دیده
          نمی‌شد و کاربر دوباره می‌زد. جمع‌شدنِ کوچک، همان تأییدِ
          فوریِ لمس است.

          ⚠️ قاعدهٔ `prefers-reduced-motion` در `tokens.css` مدت را
          صفر می‌کند، پس برای کاربری که حرکت کمتر خواسته بی‌اثر است.
        */
        'active:scale-[0.98]',
        /*
          ⚠️ برچسب دکمه هرگز نمی‌شکند.

          🔴 دکمهٔ `fullWidth` در ستونی باریک، متنِ بلندش را دو خطی
          می‌کرد و ارتفاعِ ثابتِ اندازه (`h-9.5`) آن را می‌بُرید؛ با
          آیکون بدتر هم می‌شد چون آیکون کنارِ خطِ اول می‌ماند و از
          خطِ دوم جدا می‌افتاد. در آزمون مرورگر روی «برگشت برای
          اصلاح» دقیقاً همین دیده شد.
        */
        'whitespace-nowrap',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        VARIANTS[variant],
        iconOnly ? ICON_ONLY_SIZES[size] : SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && !spinnerAtEnd ? <Spinner /> : iconStart}
      {/*
        ⚠️ برچسب در ظرفِ خودش می‌نشیند تا بتواند کوتاه شود.

        🔴 `whitespace-nowrap` روی خودِ دکمه جلوی دوخطی‌شدن را می‌گیرد
        ولی راهِ فرار نمی‌دهد: متنِ بلندتر از دکمه، از کادر **بیرون
        می‌زند** و روی دکمهٔ بغلی می‌افتد. با `min-w-0` + `truncate`
        روی خودِ برچسب، متن به‌جای شکستن یا سرریز، با «…» کوتاه
        می‌شود و ارتفاعِ ثابتِ دکمه محفوظ می‌ماند.

        ⚠️ ظرف فقط وقتی ساخته می‌شود که `children` باشد؛ دکمهٔ
        فقط‌آیکون نباید یک `<span>` خالیِ `flex-1` بگیرد که آیکونش را
        از مرکز بیرون می‌بَرد.
      */}
      {children != null && children !== false && (
        <span className="min-w-0 truncate">{children}</span>
      )}
      {spinnerAtEnd ? <Spinner /> : !loading && iconEnd}
    </button>
  )
})

/** دکمه فقط آیکون — الزام دسترس‌پذیری: label اجباری است */
export interface IconButtonProps extends Omit<ButtonProps, 'iconStart' | 'iconEnd' | 'children'> {
  label: string
  icon: ReactNode
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', className, ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      size={size}
      iconOnly
      aria-label={label}
      title={label}
      /* shrink-0 حیاتی است: دکمه آیکونی اندازه ثابت دارد و اگر داخل یک
         ردیف flex پرازدحام (مثل نوار بالا در موبایل) قرار بگیرد، بدون
         این کلاس فشرده می‌شود و آیکون بیضی و کوچک دیده می‌شود. */
      className={cn('shrink-0', className)}
      {...rest}
    >
      {icon}
    </Button>
  )
})
